/**
 * App lock (ADR-0021): set a PIN on first use; ask for it after the
 * configured idle time or when the app returns from the background.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Banner, Button, Field, Input } from '../../components';
import { useI18n } from '../../lib/i18n';
import { hasPin, IdleTracker, isValidPin, MAX_PIN_FAILURES, setPin, unlockWithBiometrics, verifyPin } from '../../lib/security/app-lock';
import { useServices } from '../../app/services';
import { signOut } from '../../app/session-actions';
import { AuthLayout } from './AuthLayout';

export function AppLockGate({ lockAfterMinutes, children }: { lockAfterMinutes: number; children: ReactNode }) {
  const [pinSet, setPinSet] = useState(hasPin);
  const [locked, setLocked] = useState(false);
  const tracker = useRef(new IdleTracker(lockAfterMinutes * 60_000));

  useEffect(() => {
    tracker.current = new IdleTracker(lockAfterMinutes * 60_000);
  }, [lockAfterMinutes]);

  useEffect(() => {
    const touch = () => tracker.current.touch();
    const check = () => {
      if (hasPin() && tracker.current.shouldLock()) setLocked(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
      else tracker.current.touch();
    };
    window.addEventListener('pointerdown', touch, { passive: true });
    window.addEventListener('keydown', touch);
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('resume', check);
    const timer = setInterval(check, 15_000);
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('resume', check);
      clearInterval(timer);
    };
  }, []);

  const unlock = useCallback(() => {
    tracker.current.touch();
    setLocked(false);
  }, []);

  if (!pinSet) return <SetPinScreen onDone={() => setPinSet(true)} />;
  if (locked) return <LockScreen onUnlock={unlock} />;
  return <>{children}</>;
}

export function SetPinScreen({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const { t } = useI18n();
  const [pin, setPinValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidPin(pin)) return setError('ui.lock.weakPin');
    if (pin !== confirm) return setError('ui.lock.mismatch');
    await setPin(pin);
    onDone();
  };
  return (
    <AuthLayout>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-xl font-bold">{t('ui.lock.setTitle')}</h2>
        <p className="text-muted">{t('ui.lock.setIntro')}</p>
        {error && <Banner tone="danger">{t(error)}</Banner>}
        <Field label={t('ui.lock.pin')}>
          {(id) => <PinInput id={id} value={pin} onChange={setPinValue} autoFocus />}
        </Field>
        <Field label={t('ui.lock.confirmPin')}>{(id) => <PinInput id={id} value={confirm} onChange={setConfirm} />}</Field>
        <Button type="submit" size="lg" block disabled={pin.length < 4}>
          {t('ui.common.save')}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            {t('ui.common.cancel')}
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}

function LockScreen({ onUnlock }: { onUnlock: () => void }) {
  const { t } = useI18n();
  const services = useServices();
  const [pin, setPinValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    unlockWithBiometrics().then((ok) => ok && onUnlock());
  }, [onUnlock]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await verifyPin(pin);
    setBusy(false);
    if (r.ok) return onUnlock();
    setPinValue('');
    if (r.failures >= MAX_PIN_FAILURES) {
      await signOut(services);
      return;
    }
    setError(t('ui.lock.wrongPin', { left: MAX_PIN_FAILURES - r.failures }));
  };

  return (
    <AuthLayout>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-xl font-bold">{t('ui.lock.lockedTitle')}</h2>
        {error && <Banner tone="danger">{error}</Banner>}
        <Field label={t('ui.lock.pin')}>{(id) => <PinInput id={id} value={pin} onChange={setPinValue} autoFocus />}</Field>
        <Button type="submit" size="lg" block loading={busy} disabled={pin.length < 4}>
          {t('ui.lock.unlock')}
        </Button>
        <Button variant="ghost" onClick={() => signOut(services)}>
          {t('ui.profile.signOut')}
        </Button>
      </form>
    </AuthLayout>
  );
}

function PinInput({ id, value, onChange, autoFocus }: { id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <Input
      id={id}
      type="password"
      inputMode="numeric"
      autoComplete="off"
      maxLength={8}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      className="text-center text-2xl tracking-[0.5em]"
    />
  );
}
